import React from 'react';
import {
  Mountain, Heart, MessageCircle, Star, BookOpen, Activity, Flame,
  Mic, Volume2, Leaf, ShoppingBag, Sparkles, Play, CheckCircle2,
} from 'lucide-react';

// Shared shell that mimics a browser window chrome
const Window = ({ children, accent = 'bg-white' }) => (
  <div className={`absolute left-3 right-3 top-5 bottom-8 ${accent} rounded-xl shadow-lg overflow-hidden border border-white/60`}>
    <div className="flex items-center gap-1 px-2.5 py-1.5 border-b border-neutral-100 bg-white/70 backdrop-blur">
      <div className="w-1.5 h-1.5 rounded-full bg-rose-300" />
      <div className="w-1.5 h-1.5 rounded-full bg-amber-300" />
      <div className="w-1.5 h-1.5 rounded-full bg-emerald-300" />
    </div>
    <div className="h-[calc(100%-1.6rem)] overflow-hidden">{children}</div>
  </div>
);

// 1. TrekVibe — Hiking community feed
const TrekVibePreview = () => (
  <Window>
    <div className="p-2.5 space-y-2">
      <div className="flex items-center gap-1.5">
        <Mountain className="w-3 h-3 text-emerald-600" />
        <span className="text-[10px] font-bold text-neutral-900">TrekVibe</span>
        <span className="ml-auto text-[8px] text-neutral-400">Feed</span>
      </div>
      <div className="rounded-lg border border-neutral-100 overflow-hidden">
        <div className="h-14 bg-gradient-to-br from-emerald-300 via-teal-300 to-cyan-300 relative">
          <div className="absolute bottom-0 left-0 right-0 h-6 bg-[radial-gradient(ellipse_at_bottom,rgba(0,0,0,0.25),transparent_70%)]" />
          <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded-full bg-white/90 text-[7px] font-semibold text-emerald-700">Hard</div>
        </div>
        <div className="p-1.5 space-y-1">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-gradient-to-br from-orange-300 to-rose-400" />
            <div className="h-1.5 w-12 rounded bg-neutral-800" />
          </div>
          <div className="h-1 w-20 rounded bg-neutral-200" />
          <div className="flex items-center gap-2 pt-0.5">
            <div className="flex items-center gap-0.5"><Heart className="w-2 h-2 text-rose-400" /><span className="text-[7px] text-neutral-500">128</span></div>
            <div className="flex items-center gap-0.5"><MessageCircle className="w-2 h-2 text-neutral-400" /><span className="text-[7px] text-neutral-500">24</span></div>
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-neutral-100 p-1.5 space-y-1">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-gradient-to-br from-sky-300 to-indigo-400" />
          <div className="h-1.5 w-10 rounded bg-neutral-800" />
          <div className="ml-auto px-1 py-0.5 rounded-full bg-emerald-100 text-[6px] font-semibold text-emerald-700">Easy</div>
        </div>
        <div className="h-1 w-16 rounded bg-neutral-200" />
      </div>
    </div>
  </Window>
);

// 2. BookHub — Book marketplace grid
const BookHubPreview = () => (
  <Window>
    <div className="p-2.5 space-y-2">
      <div className="flex items-center gap-1.5">
        <BookOpen className="w-3 h-3 text-amber-600" />
        <span className="text-[10px] font-bold text-neutral-900">BookHub</span>
        <div className="ml-auto h-2.5 w-10 rounded-full bg-neutral-100" />
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {[
          'from-rose-400 to-pink-500',
          'from-amber-400 to-orange-500',
          'from-violet-400 to-purple-600',
          'from-emerald-400 to-teal-500',
          'from-sky-400 to-indigo-500',
          'from-yellow-300 to-amber-500',
        ].map((g, i) => (
          <div key={i} className="space-y-0.5">
            <div className={`aspect-[3/4] rounded-md bg-gradient-to-br ${g} relative overflow-hidden shadow-sm`}>
              <div className="absolute inset-x-1 top-1 h-0.5 bg-white/50 rounded" />
              <div className="absolute inset-x-1 top-2 h-0.5 w-2/3 bg-white/40 rounded" />
              <div className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-t from-black/30 to-transparent" />
            </div>
            <div className="flex items-center gap-0.5">
              <Star className="w-1.5 h-1.5 text-amber-400 fill-amber-400" />
              <span className="text-[6px] font-medium text-neutral-600">4.{8 - i}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </Window>
);

// 3. PulseFit — Fitness dashboard
const PulseFitPreview = () => (
  <Window>
    <div className="p-2.5 space-y-2">
      <div className="flex items-center gap-1.5">
        <Activity className="w-3 h-3 text-rose-500" />
        <span className="text-[10px] font-bold text-neutral-900">PulseFit</span>
        <div className="ml-auto flex items-center gap-0.5">
          <Flame className="w-2 h-2 text-orange-500" />
          <span className="text-[8px] font-bold text-neutral-700">12</span>
        </div>
      </div>
      <div className="rounded-lg bg-gradient-to-br from-rose-50 to-fuchsia-50 p-2 flex items-center gap-2">
        <div className="relative w-10 h-10">
          <div className="w-10 h-10 rounded-full border-[3px] border-neutral-200" />
          <div className="absolute inset-0 w-10 h-10 rounded-full border-[3px] border-rose-500 border-r-transparent border-b-transparent -rotate-45" />
          <div className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-neutral-900">72%</div>
        </div>
        <div className="flex-1 space-y-1">
          <div className="h-1.5 w-14 rounded bg-neutral-800" />
          <div className="h-1 w-10 rounded bg-neutral-300" />
          <div className="h-1 w-full rounded-full bg-neutral-200 overflow-hidden">
            <div className="h-full w-3/4 bg-gradient-to-r from-rose-400 to-fuchsia-500" />
          </div>
        </div>
      </div>
      <div className="space-y-1">
        {['Push Day', 'Cardio HIIT'].map((w, i) => (
          <div key={i} className="flex items-center gap-1.5 rounded-md bg-neutral-50 px-1.5 py-1">
            <div className="w-3 h-3 rounded bg-gradient-to-br from-rose-400 to-fuchsia-500 flex items-center justify-center">
              <Play className="w-1.5 h-1.5 text-white fill-white" />
            </div>
            <div className="text-[7px] font-semibold text-neutral-800">{w}</div>
            <span className="ml-auto text-[6px] text-neutral-400">45m</span>
          </div>
        ))}
      </div>
    </div>
  </Window>
);

// 4. LedgerCRM — Kanban pipeline
const LedgerCRMPreview = () => (
  <Window>
    <div className="p-2 space-y-1.5">
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-bold text-neutral-900">LedgerCRM</span>
        <div className="ml-auto h-2 w-6 rounded bg-neutral-900" />
      </div>
      <div className="grid grid-cols-3 gap-1">
        {[
          { label: 'Lead', color: 'bg-sky-100 text-sky-700', cards: 3, bar: 'bg-sky-400' },
          { label: 'Demo', color: 'bg-indigo-100 text-indigo-700', cards: 2, bar: 'bg-indigo-400' },
          { label: 'Won', color: 'bg-emerald-100 text-emerald-700', cards: 2, bar: 'bg-emerald-400' },
        ].map((col, i) => (
          <div key={i} className="space-y-1">
            <div className={`px-1 py-0.5 rounded text-[7px] font-bold ${col.color}`}>{col.label}</div>
            {Array.from({ length: col.cards }).map((_, j) => (
              <div key={j} className="rounded-md border border-neutral-100 bg-white p-1 space-y-0.5 shadow-sm">
                <div className="h-0.5 w-full rounded bg-neutral-800" />
                <div className="h-0.5 w-2/3 rounded bg-neutral-200" />
                <div className={`h-0.5 w-1/2 rounded ${col.bar} mt-0.5`} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </Window>
);

// 5. NoteNest — AI note editor
const NoteNestPreview = () => (
  <Window>
    <div className="flex h-full">
      <div className="w-1/3 bg-neutral-50 p-1.5 space-y-1 border-r border-neutral-100">
        <div className="text-[8px] font-bold text-neutral-900">Notebooks</div>
        {['Ideas', 'Daily', 'Research'].map((n, i) => (
          <div key={i} className={`px-1 py-0.5 rounded text-[7px] ${i === 0 ? 'bg-violet-100 text-violet-700 font-semibold' : 'text-neutral-600'}`}>{n}</div>
        ))}
      </div>
      <div className="flex-1 p-2 space-y-1.5">
        <div className="h-1.5 w-3/4 rounded bg-neutral-900" />
        <div className="h-1 w-full rounded bg-neutral-200" />
        <div className="h-1 w-5/6 rounded bg-neutral-200" />
        <div className="h-1 w-4/6 rounded bg-neutral-200" />
        <div className="mt-1 rounded-md bg-gradient-to-r from-violet-100 to-pink-100 border border-violet-200 p-1 flex items-center gap-1">
          <Sparkles className="w-2 h-2 text-violet-600" />
          <span className="text-[6px] font-semibold text-violet-700">Summarize</span>
          <span className="text-[6px] text-violet-500">· Expand</span>
        </div>
      </div>
    </div>
  </Window>
);

// 6. GreenCart — Eco grocery store
const GreenCartPreview = () => (
  <Window>
    <div className="p-2 space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Leaf className="w-3 h-3 text-emerald-600" />
        <span className="text-[10px] font-bold text-neutral-900">GreenCart</span>
        <ShoppingBag className="w-2.5 h-2.5 ml-auto text-neutral-700" />
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {[
          { g: 'from-lime-200 to-emerald-300', name: 'Avocado', co2: 'A+' },
          { g: 'from-orange-200 to-amber-300', name: 'Oats', co2: 'A' },
          { g: 'from-rose-200 to-pink-300', name: 'Berries', co2: 'A+' },
          { g: 'from-emerald-200 to-teal-300', name: 'Kale', co2: 'A' },
        ].map((p, i) => (
          <div key={i} className="rounded-md border border-neutral-100 overflow-hidden">
            <div className={`h-8 bg-gradient-to-br ${p.g} relative`}>
              <div className="absolute top-0.5 right-0.5 px-1 py-0 rounded-full bg-white/90 text-[6px] font-bold text-emerald-700">{p.co2}</div>
            </div>
            <div className="p-1 space-y-0.5">
              <div className="text-[7px] font-semibold text-neutral-800">{p.name}</div>
              <div className="text-[6px] font-bold text-emerald-700">$3.{4 + i}9</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </Window>
);

// 7. SonicTutor — Language lesson
const SonicTutorPreview = () => (
  <Window>
    <div className="p-2.5 space-y-2">
      <div className="flex items-center gap-1.5">
        <Volume2 className="w-3 h-3 text-orange-500" />
        <span className="text-[10px] font-bold text-neutral-900">SonicTutor</span>
        <div className="ml-auto flex items-center gap-0.5">
          <Flame className="w-2 h-2 text-orange-500" />
          <span className="text-[8px] font-bold text-neutral-700">7</span>
        </div>
      </div>
      <div className="rounded-lg bg-gradient-to-br from-orange-100 to-amber-100 p-2 space-y-1.5 border border-orange-200">
        <div className="text-[8px] font-semibold text-orange-700">Lesson 12 · B1</div>
        <div className="text-[11px] font-bold text-neutral-900">"Bonjour, ça va?"</div>
        <div className="h-1 w-full rounded-full bg-white/70 overflow-hidden">
          <div className="h-full w-2/3 bg-gradient-to-r from-orange-400 to-amber-500" />
        </div>
      </div>
      <div className="flex items-center justify-center gap-2">
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center shadow-md">
          <Mic className="w-3 h-3 text-white" />
        </div>
        <div className="flex gap-0.5 items-end h-4">
          {[2, 3, 2, 4, 3, 2, 3].map((h, i) => (
            <div key={i} className="w-0.5 rounded-full bg-orange-400" style={{ height: `${h * 3}px` }} />
          ))}
        </div>
      </div>
    </div>
  </Window>
);

// 8. PixelDeck — Slide deck builder
const PixelDeckPreview = () => (
  <Window>
    <div className="p-2 space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Sparkles className="w-3 h-3 text-cyan-600" />
        <span className="text-[10px] font-bold text-neutral-900">PixelDeck</span>
        <div className="ml-auto px-1 py-0.5 rounded bg-neutral-900 text-[6px] text-white font-semibold">Export</div>
      </div>
      <div className="rounded-lg aspect-[16/9] bg-gradient-to-br from-cyan-100 via-sky-50 to-blue-100 border border-cyan-200 p-2 flex flex-col justify-center relative overflow-hidden">
        <div className="absolute top-1 right-1 w-3 h-3 rounded-full bg-cyan-400/40" />
        <div className="absolute bottom-2 right-2 w-4 h-4 rounded-full bg-blue-400/30" />
        <div className="h-1.5 w-3/4 rounded bg-neutral-900" />
        <div className="mt-1 space-y-0.5">
          <div className="flex items-center gap-1"><div className="w-0.5 h-0.5 rounded-full bg-cyan-600" /><div className="h-0.5 w-12 rounded bg-neutral-400" /></div>
          <div className="flex items-center gap-1"><div className="w-0.5 h-0.5 rounded-full bg-cyan-600" /><div className="h-0.5 w-10 rounded bg-neutral-400" /></div>
          <div className="flex items-center gap-1"><div className="w-0.5 h-0.5 rounded-full bg-cyan-600" /><div className="h-0.5 w-14 rounded bg-neutral-400" /></div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`aspect-[16/9] rounded ${i === 0 ? 'ring-1 ring-cyan-500' : ''} bg-gradient-to-br ${
            ['from-cyan-100 to-blue-100', 'from-sky-100 to-indigo-100', 'from-blue-100 to-violet-100', 'from-indigo-100 to-cyan-100'][i]
          } border border-neutral-200`} />
        ))}
      </div>
    </div>
  </Window>
);

const PREVIEWS = {
  1: TrekVibePreview,
  2: BookHubPreview,
  3: PulseFitPreview,
  4: LedgerCRMPreview,
  5: NoteNestPreview,
  6: GreenCartPreview,
  7: SonicTutorPreview,
  8: PixelDeckPreview,
};

export default function TemplatePreview({ id }) {
  const Component = PREVIEWS[id];
  if (!Component) return null;
  return <Component />;
}

export const HAS_PREVIEW = (id) => Boolean(PREVIEWS[id]);
